import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import RoboticsShell from "./RoboticsShell";
import { api } from "../../lib/api";

/**
 * Robotics lesson page — Phase 4 multi-band (Explorer + Builder).
 *
 * URL patterns:
 *   /robotics/lesson/e{term}w{week}l{idx}   → Explorer band
 *   /robotics/lesson/b{term}w{week}l{idx}   → Builder band
 *
 * Sidebar shows BOTH bands with the non-current band collapsed by default.
 * Prev/Next crosses band boundary: last Explorer → first Builder, and back.
 */
const BAND_META = {
  explorer: { key: "explorer", prefix: "e", label: "Explorer", ageBand: "8–11", terms: 3 },
  builder:  { key: "builder",  prefix: "b", label: "Builder",  ageBand: "12–14", terms: 3 },
  engineer: { key: "engineer", prefix: "n", label: "Engineer", ageBand: "15–17", terms: 3 },
};
const BAND_ORDER = ["explorer", "builder", "engineer"];

function bandFromId(id) {
  const prefix = (id || "").charAt(0).toLowerCase();
  return BAND_ORDER.find((b) => BAND_META[b].prefix === prefix) || "explorer";
}

// --- Arduino / cpp keyword highlighter (CSS-only, no Prism dep) ---
const CPP_KEYWORDS = new Set([
  "int","void","if","else","for","while","return","const","true","false",
  "byte","boolean","char","long","short","unsigned","float","double","struct",
  "static","break","continue","switch","case","default","do","new","delete",
]);
const ARDUINO_FUNCS = new Set([
  "setup","loop","pinMode","digitalWrite","digitalRead","analogRead","analogWrite",
  "Serial","begin","print","println","available","read","write","delay","delayMicroseconds",
  "millis","micros","map","constrain","attach","writeMicroseconds","tone","noTone",
  "Wire","attachInterrupt","detachInterrupt","INPUT","OUTPUT","HIGH","LOW",
  "INPUT_PULLUP","LED_BUILTIN","A0","A1","A2","A3","A4","A5",
]);

function highlightCpp(src) {
  const tokens = [];
  const re = /(\/\/[^\n]*|\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\b\d+(?:\.\d+)?\b|\b[A-Za-z_]\w*\b|\s+|.)/g;
  let m;
  while ((m = re.exec(src)) !== null) {
    const tk = m[0];
    if (/^\/\//.test(tk) || /^\/\*/.test(tk)) tokens.push({ t: "cmt", v: tk });
    else if (/^["']/.test(tk)) tokens.push({ t: "str", v: tk });
    else if (/^\d/.test(tk)) tokens.push({ t: "num", v: tk });
    else if (/^\s+$/.test(tk)) tokens.push({ t: "ws", v: tk });
    else if (CPP_KEYWORDS.has(tk)) tokens.push({ t: "kw", v: tk });
    else if (ARDUINO_FUNCS.has(tk)) tokens.push({ t: "fn", v: tk });
    else tokens.push({ t: "id", v: tk });
  }
  return tokens;
}

function CodeBlock({ snippet }) {
  if (!snippet || !snippet.snippet) return null;
  const tokens = highlightCpp(snippet.snippet);
  return (
    <div className="rbx-lesson__code" data-testid="rbx-lesson-code">
      <div className="rbx-lesson__code-head">
        <span className="rbx-lesson__code-lang">{(snippet.language || "cpp").toUpperCase()}</span>
        <span className="rbx-lesson__code-hint">Arduino sketch</span>
      </div>
      <pre className="rbx-lesson__code-pre"><code>
        {tokens.map((tk, i) => (
          <span key={i} className={`rbx-tok rbx-tok--${tk.t}`}>{tk.v}</span>
        ))}
      </code></pre>
      {snippet.explanation && (
        <p className="rbx-lesson__code-expl" data-testid="rbx-lesson-code-expl">{snippet.explanation}</p>
      )}
    </div>
  );
}

export default function RoboticsLesson() {
  const { id = "e1w1l1" } = useParams();
  const band = bandFromId(id);
  const [lesson, setLesson] = useState(null);
  const [indexByBand, setIndexByBand] = useState({ explorer: [], builder: [], engineer: [] });
  const [state, setState] = useState("loading");
  const [collapsed, setCollapsed] = useState({ explorer: false, builder: false, engineer: false });

  // Collapse the OTHER bands by default whenever we switch.
  useEffect(() => {
    setCollapsed({
      explorer: band !== "explorer",
      builder:  band !== "builder",
      engineer: band !== "engineer",
    });
  }, [band]);

  useEffect(() => {
    let alive = true;
    setState("loading");
    Promise.all([
      api.get(`/robotics/lessons/${id}`).then((r) => r.data),
      api.get(`/robotics/lessons?band=explorer`).then((r) => r.data.items || []),
      api.get(`/robotics/lessons?band=builder`).then((r) => r.data.items || []),
      api.get(`/robotics/lessons?band=engineer`).then((r) => r.data.items || []),
    ])
      .then(([L, exp, bld, eng]) => {
        if (!alive) return;
        setLesson(L);
        setIndexByBand({ explorer: exp, builder: bld, engineer: eng });
        setState("ok");
      })
      .catch((e) => {
        if (!alive) return;
        setState(e?.response?.status === 404 ? "notfound" : "error");
        // Still populate sidebar so the user can navigate.
        Promise.all([
          api.get(`/robotics/lessons?band=explorer`).then((r) => r.data.items || []).catch(() => []),
          api.get(`/robotics/lessons?band=builder`).then((r) => r.data.items || []).catch(() => []),
          api.get(`/robotics/lessons?band=engineer`).then((r) => r.data.items || []).catch(() => []),
        ]).then(([exp, bld, eng]) => setIndexByBand({ explorer: exp, builder: bld, engineer: eng }));
      });
    return () => { alive = false; };
  }, [id]);

  // Global order: full Explorer, then Builder, then Engineer. Prev/next
  // across band boundaries falls out for free.
  const flat = useMemo(
    () => [
      ...(indexByBand.explorer || []),
      ...(indexByBand.builder  || []),
      ...(indexByBand.engineer || []),
    ],
    [indexByBand]
  );
  const currentPos = useMemo(() => flat.findIndex((x) => x.lesson_id === id), [flat, id]);
  const prev = currentPos > 0 ? flat[currentPos - 1] : null;
  const next = currentPos >= 0 && currentPos < flat.length - 1 ? flat[currentPos + 1] : null;

  const meta = BAND_META[band];

  return (
    <RoboticsShell>
      <section className="rbx-lesson" data-testid="rbx-page-lesson">
        <div>
          <div className="rbx-lesson__breadcrumb" data-testid="rbx-lesson-breadcrumb">
            {lesson
              ? `${meta.label} · Term ${lesson.term} · Week ${lesson.week} · Lesson ${lesson.index_in_week}`
              : `${meta.label} band`}
          </div>

          {state === "loading" && (
            <div data-testid="rbx-lesson-skeleton">
              <div style={{ height: 44, background: "var(--rbx-bg-elev)", borderRadius: 10, marginBottom: 20, opacity: 0.55 }} />
              <div style={{ height: 160, background: "var(--rbx-bg-elev)", borderRadius: 14, marginBottom: 20, opacity: 0.45 }} />
              <div style={{ height: 280, background: "var(--rbx-bg-elev)", borderRadius: 14, opacity: 0.35 }} />
            </div>
          )}

          {state === "notfound" && (
            <>
              <h1 className="rbx-lesson__title" data-testid="rbx-lesson-notfound">Lesson not found.</h1>
              <p style={{ color: "var(--rbx-ink-mute)" }}>
                The lesson <code style={{ color: "var(--rbx-copper)", fontFamily: "var(--rbx-font-mono)" }}>{id}</code> doesn&rsquo;t exist. Pick one from the index on the right.
              </p>
              <Link className="rbx-btn rbx-btn--ghost" to="/robotics/dashboard" data-testid="rbx-lesson-back" style={{ marginTop: 20 }}>← Back to workbench</Link>
            </>
          )}

          {state === "ok" && lesson && (
            <>
              <h1 className="rbx-lesson__title" data-testid="rbx-lesson-title">{lesson.title}</h1>

              {lesson.components?.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10, marginBottom: 24 }}
                     data-testid="rbx-lesson-components">
                  {lesson.components.map((c) => (
                    <div key={c.slug} className="rbx-card" style={{ padding: 10, textAlign: "center" }} data-testid={`rbx-comp-${c.slug}`}>
                      <img src={`/robotics/components/${c.filename}`} alt={c.slug} style={{ width: "100%", height: 70, objectFit: "contain" }} />
                      <div className="rbx-band__name" style={{ fontSize: 10, marginTop: 6 }}>{c.slug}</div>
                      <div style={{ fontSize: 11, color: "var(--rbx-ink-mute)", lineHeight: 1.4 }}>{c.description}</div>
                    </div>
                  ))}
                </div>
              )}

              <Section label="Learning objective" testId="rbx-lo">{lesson.learning_objective}</Section>
              <Section label="First-principles — the WHY" testId="rbx-fp">{lesson.first_principles_explanation}</Section>
              <Section label="Hands-on activity" testId="rbx-ho">{lesson.hands_on_activity}</Section>

              <CodeBlock snippet={lesson.code_snippet} />

              {lesson.design_tradeoff && (
                <div className="rbx-lesson__tradeoff" data-testid="rbx-lesson-tradeoff">
                  <div className="rbx-lesson__tradeoff-head">
                    <span className="rbx-lesson__tradeoff-tag">TRADE-OFF</span>
                    <span className="rbx-lesson__tradeoff-choice">{lesson.design_tradeoff.choice}</span>
                  </div>
                  <ul className="rbx-lesson__tradeoff-options">
                    {(lesson.design_tradeoff.options || []).map((o, i) => (
                      <li key={i}>{o}</li>
                    ))}
                  </ul>
                  {lesson.design_tradeoff.engineer_reflection && (
                    <p className="rbx-lesson__tradeoff-reflection">
                      <em>Senior engineer says:</em> {lesson.design_tradeoff.engineer_reflection}
                    </p>
                  )}
                </div>
              )}

              {lesson.circuitverse_spec && (
                <div className="rbx-lesson__sim" data-testid="rbx-lesson-sim">
                  CircuitVerse · <span style={{ color: "var(--rbx-gold)" }}>{lesson.circuitverse_spec.circuitverse_url_or_slug}</span>
                  <div style={{ marginTop: 6, color: "var(--rbx-ink-mute)", textTransform: "none", letterSpacing: 0, fontSize: 12.5, fontFamily: "var(--rbx-font-body)" }}>
                    {lesson.circuitverse_spec.student_goal}
                  </div>
                </div>
              )}

              <Section label="Try at home" testId="rbx-wc">{lesson.workbench_challenge}</Section>

              <div className="rbx-lesson__body" data-testid="rbx-lesson-checks" style={{ marginTop: 20 }}>
                <h3 style={{ fontFamily: "var(--rbx-font-mono)", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--rbx-gold)", margin: "0 0 12px" }}>
                  Quick check
                </h3>
                {(lesson.comprehension_checks || []).map((c, i) => (
                  <details key={i} style={{ marginBottom: 12, background: "rgba(46,229,157,0.04)", borderLeft: "2px solid var(--rbx-phosphor)", padding: "10px 14px", borderRadius: 6 }} data-testid={`rbx-check-${i}`}>
                    <summary style={{ cursor: "pointer", color: "var(--rbx-ink)", fontWeight: 600 }}>{c.q}</summary>
                    <div style={{ marginTop: 8, color: "var(--rbx-phosphor)" }}><strong>Answer:</strong> {c.a}</div>
                    <div style={{ marginTop: 4, color: "var(--rbx-ink-mute)", fontSize: 13.5 }}>{c.explanation}</div>
                  </details>
                ))}
              </div>

              {lesson.girl_inclusive_example && (
                <Section label="A story from the bench" testId="rbx-gie" tone="coral">{lesson.girl_inclusive_example}</Section>
              )}
              {lesson.teacher_note && (
                <Section label="For parents & teachers" testId="rbx-tn" tone="gold">{lesson.teacher_note}</Section>
              )}

              <div className="rbx-lesson__nav-next">
                {prev
                  ? <Link className="rbx-btn rbx-btn--ghost" to={`/robotics/lesson/${prev.lesson_id}`} data-testid="rbx-lesson-prev">← {prev.title}</Link>
                  : <span />}
                {next
                  ? <Link className="rbx-btn rbx-btn--primary" to={`/robotics/lesson/${next.lesson_id}`} data-testid="rbx-lesson-next">{next.title} →</Link>
                  : <span />}
              </div>
            </>
          )}
        </div>

        <aside className="rbx-lesson__sidebar" data-testid="rbx-lesson-index">
          {BAND_ORDER.map((b) => {
            const bandMeta = BAND_META[b];
            const isCollapsed = collapsed[b];
            const bandLessons = indexByBand[b] || [];
            return (
              <div key={b} className="rbx-card" style={{ padding: 14 }} data-testid={`rbx-idx-band-${b}`}>
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => ({ ...c, [b]: !c[b] }))}
                  className="rbx-lesson__sidebar-head"
                  data-testid={`rbx-idx-band-toggle-${b}`}
                  aria-expanded={!isCollapsed}
                >
                  <span style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ color: b === band ? "var(--rbx-gold)" : "var(--rbx-ink)", fontWeight: 700, fontFamily: "var(--rbx-font-display)", fontSize: 15 }}>
                      {bandMeta.label} band
                    </span>
                    <span style={{ color: "var(--rbx-ink-mute)", fontSize: 11.5, letterSpacing: 0.06, fontFamily: "var(--rbx-font-mono)" }}>
                      Ages {bandMeta.ageBand} · {bandLessons.length} lessons
                    </span>
                  </span>
                  <span style={{ color: "var(--rbx-copper)", fontFamily: "var(--rbx-font-mono)", fontSize: 11 }}>
                    {isCollapsed ? "▸ expand" : "▾ collapse"}
                  </span>
                </button>
                {!isCollapsed && [1, 2, 3].map((t) => {
                  const rows = bandLessons.filter((x) => x.term === t);
                  if (rows.length === 0) return null;
                  return (
                    <div key={t} style={{ marginTop: 10 }}>
                      <h4 style={{ fontFamily: "var(--rbx-font-mono)", fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--rbx-copper)", margin: "0 0 6px" }}>Term {t}</h4>
                      <ol style={{ paddingLeft: 18, margin: 0, color: "var(--rbx-ink-mute)", fontSize: 12.5, lineHeight: 1.55 }}>
                        {rows.map((x) => (
                          <li key={x.lesson_id} style={{ margin: "3px 0" }}>
                            <Link to={`/robotics/lesson/${x.lesson_id}`}
                                  style={{
                                    color: x.lesson_id === id ? "var(--rbx-gold)" : "var(--rbx-ink-mute)",
                                    fontWeight: x.lesson_id === id ? 700 : 400,
                                    textDecoration: "none",
                                  }}
                                  data-testid={`rbx-idx-${x.lesson_id}`}>
                              {x.title}
                            </Link>
                          </li>
                        ))}
                      </ol>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </aside>
      </section>
    </RoboticsShell>
  );
}

function Section({ label, children, testId, tone }) {
  const borderColor = tone === "coral" ? "var(--rbx-coral)" : tone === "gold" ? "var(--rbx-gold)" : "var(--rbx-copper)";
  return (
    <div style={{ marginTop: 20, padding: "16px 20px", background: "var(--rbx-bg-elev)", borderLeft: `3px solid ${borderColor}`, borderRadius: 8 }} data-testid={testId}>
      <div style={{ fontFamily: "var(--rbx-font-mono)", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: borderColor, marginBottom: 8 }}>{label}</div>
      <div style={{ color: "var(--rbx-ink)", lineHeight: 1.6, fontSize: 14.5 }}>{children}</div>
    </div>
  );
}
